import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { localName } from "../lib/localName";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import {
  Plus, Building2, Image as ImageIcon, MapPin, MapPinned,
  Map as MapIcon, Grid3x3, GraduationCap, Users, BookOpen, Briefcase,
  CheckCircle2, AlertCircle, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";

const EMPTY = {
  name: "", name_ar: "", country: "SA", city: "", address: "",
  currency: "SAR", logo_id: "",
  geo_lat: "", geo_lng: "", geo_radius_m: 150, day_start: "07:30",
};

export default function SchoolsPage() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [view, setView] = useState("grid"); // grid | map
  const [mapsKey, setMapsKey] = useState("");

  const load = () => api.get("/schools/with-stats").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/maps/key").then(r => setMapsKey(r.data?.key || "")).catch(() => {});
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...EMPTY, ...it,
      geo_lat: it.geo_lat ?? "",
      geo_lng: it.geo_lng ?? "",
      geo_radius_m: it.geo_radius_m ?? 150,
      day_start: it.day_start ?? "07:30",
    });
    setOpen(true);
  };
  const submit = async () => {
    try {
      const payload = {
        ...form,
        geo_lat: form.geo_lat === "" ? null : Number(form.geo_lat),
        geo_lng: form.geo_lng === "" ? null : Number(form.geo_lng),
        geo_radius_m: Number(form.geo_radius_m) || 150,
      };
      if (editing) await api.put(`/schools/${editing.id}`, payload);
      else await api.post("/schools", payload);
      toast.success(t("success") || "تم الحفظ"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const remove = async (it) => {
    try { await api.delete(`/schools/${it.id}`); toast.success(t("success") || "تم"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, logo_id: data.id });
      toast.success(t("success") || "تم");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setUploading(false);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error(t("location_unsupported")); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm({
          ...form,
          geo_lat: pos.coords.latitude.toFixed(6),
          geo_lng: pos.coords.longitude.toFixed(6),
        });
        toast.success(`${t("location_acquired")} ${Math.round(pos.coords.accuracy)} ${t("meters")}`);
        setLocating(false);
      },
      (err) => { toast.error(`${t("location_failed")}: ${err.message}`); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="space-y-6" data-testid="schools-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">{t("schools")}</h1>
        <div className="flex gap-2">
          <div className="inline-flex rounded-md border border-border p-0.5">
            <Button
              variant={view === "grid" ? "default" : "ghost"}
              size="sm"
              onClick={() => setView("grid")}
              data-testid="schools-view-grid"
            ><Grid3x3 className="w-4 h-4 me-2" />{t("show_grid")}</Button>
            <Button
              variant={view === "map" ? "default" : "ghost"}
              size="sm"
              onClick={() => setView("map")}
              data-testid="schools-view-map"
            ><MapIcon className="w-4 h-4 me-2" />{t("show_map")}</Button>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="new-school-btn">
                <Plus className="w-4 h-4 me-2" />{t("new_school")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? t("edit") : t("new_school")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>{t("nameAr")}</Label><Input data-testid="school-name-ar" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} /></div>
                  <div><Label>{t("nameEn")}</Label><Input data-testid="school-name-en" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label>{t("currency")}</Label><Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} /></div>
                  <div><Label>{t("city")}</Label><Input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>{t("day_start")}</Label><Input type="time" value={form.day_start || "07:30"} onChange={(e) => setForm({ ...form, day_start: e.target.value })} data-testid="school-day-start" /></div>
                </div>
                <div>
                  <Label>{t("address")}</Label>
                  <Input
                    value={form.address || ""}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    placeholder={t("address_placeholder")}
                    data-testid="school-address"
                  />
                </div>
                <div>
                  <Label>{t("logo_label")}</Label>
                  <div className="flex items-center gap-3 mt-1">
                    {form.logo_id ? (
                      <img src={`${api.defaults.baseURL}/files/${form.logo_id}`} alt="logo" className="w-12 h-12 object-contain rounded-md border border-border bg-muted" crossOrigin="anonymous" />
                    ) : (
                      <div className="w-12 h-12 rounded-md border border-dashed border-border grid place-items-center text-muted-foreground"><ImageIcon className="w-5 h-5" /></div>
                    )}
                    <input type="file" accept="image/*" onChange={uploadLogo} disabled={uploading} className="text-sm" data-testid="school-logo-input" />
                  </div>
                </div>

                {/* Geofence per-school (each branch has its own coordinates) */}
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                    <MapPinned className="w-4 h-4" /> {t("geofence")}
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{t("geofence_help")}</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label>{t("latitude")}</Label>
                      <Input type="number" step="0.000001" placeholder="24.713600" value={form.geo_lat} onChange={(e) => setForm({ ...form, geo_lat: e.target.value })} data-testid="school-geo-lat" />
                    </div>
                    <div>
                      <Label>{t("longitude")}</Label>
                      <Input type="number" step="0.000001" placeholder="46.675300" value={form.geo_lng} onChange={(e) => setForm({ ...form, geo_lng: e.target.value })} data-testid="school-geo-lng" />
                    </div>
                    <div>
                      <Label>{t("radius_m")}</Label>
                      <Input type="number" min="20" max="5000" placeholder="150" value={form.geo_radius_m} onChange={(e) => setForm({ ...form, geo_radius_m: e.target.value })} data-testid="school-geo-radius" />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={useMyLocation} disabled={locating} data-testid="school-use-location" type="button">
                      {locating ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <MapPin className="w-4 h-4 me-2" />}
                      {t("use_my_location")}
                    </Button>
                    {form.geo_lat && form.geo_lng && (
                      <a
                        href={`https://www.google.com/maps?q=${form.geo_lat},${form.geo_lng}`}
                        target="_blank" rel="noreferrer"
                        className="inline-flex items-center text-sm text-primary hover:underline gap-1"
                      ><MapPinned className="w-4 h-4" /> {t("open_map") || "افتح في الخريطة"}</a>
                    )}
                  </div>
                </div>

                <Button onClick={submit} className="w-full" data-testid="school-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {view === "grid" ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="schools-grid">
          {items.map((s) => <SchoolCard key={s.id} s={s} onEdit={openEdit} onDelete={remove} t={t} />)}
          {items.length === 0 && <Card className="p-8 col-span-full text-center text-sm text-muted-foreground border-dashed">{t("no_data") || "لا توجد بيانات"}</Card>}
        </div>
      ) : (
        <SchoolsMapView items={items} mapsKey={mapsKey} t={t} onEdit={openEdit} />
      )}
    </div>
  );
}

function SchoolCard({ s, onEdit, onDelete, t }) {
  const { lang } = useI18n();
  const stats = s.stats || {};
  const configured = s.geofence_configured;
  return (
    <Card className="p-5 border border-border shadow-none card-hover smooth" data-testid={`school-${s.id}`}>
      <div className="flex items-start gap-3">
        {s.logo_id ? (
          <img src={`${api.defaults.baseURL}/files/${s.logo_id}`} alt="logo" className="w-10 h-10 object-contain rounded-md bg-muted" crossOrigin="anonymous" />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><Building2 className="w-5 h-5" /></div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{localName(s, lang)}</div>
          <div className="text-xs text-muted-foreground truncate">{s.name}</div>
          <div className="text-xs text-muted-foreground mt-1">{s.city || "—"} · {s.currency}</div>
        </div>
        <RowActions item={s} onEdit={onEdit} onDelete={onDelete} testIdPrefix="school" />
      </div>

      {/* Stats grid */}
      <div className="mt-4 grid grid-cols-4 gap-1.5 text-center">
        <StatChip icon={GraduationCap} label={t("students")} value={stats.students} color="emerald" />
        <StatChip icon={Users} label={t("teachers")} value={stats.teachers} color="blue" />
        <StatChip icon={Briefcase} label={t("employees")} value={stats.employees} color="violet" />
        <StatChip icon={BookOpen} label={t("classes")} value={stats.classes} color="amber" />
      </div>

      {/* Geofence status */}
      <div className={`mt-3 flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-md border ${configured ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30"}`}>
        {configured ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
        <span className="truncate">{configured ? `${t("geofence_configured")} · ${s.geo_radius_m || 150}م` : t("geofence_missing")}</span>
      </div>
    </Card>
  );
}

function StatChip({ icon: Icon, label, value, color }) {
  const palette = {
    emerald: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    blue: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
    violet: "bg-violet-500/10 text-violet-600 dark:text-violet-400",
    amber: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  }[color] || "bg-muted text-foreground";
  return (
    <div className={`rounded-md px-1.5 py-2 ${palette}`}>
      <Icon className="w-3.5 h-3.5 mx-auto mb-0.5" />
      <div className="text-base font-semibold leading-tight">{value ?? 0}</div>
      <div className="text-[10px] opacity-80 truncate">{label}</div>
    </div>
  );
}

/** Google Maps view of all schools. Falls back to a list with map links if no API key. */
function SchoolsMapView({ items, mapsKey, t, onEdit }) {
  const { lang } = useI18n();
  const validItems = useMemo(() => items.filter(s => s.geo_lat && s.geo_lng), [items]);

  if (!mapsKey) {
    return (
      <Card className="p-6 border shadow-none space-y-4">
        <div className="text-sm text-muted-foreground flex items-start gap-2">
          <MapPinned className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
          <span>{t("enable_maps_note")} <b>Google Maps API</b> ← <a href="/settings" className="text-primary underline">{t("settings_maps_link")}</a>.</span>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map(s => (
            <Card key={s.id} className="p-3 border" data-testid={`map-list-${s.id}`}>
              <div className="font-semibold text-sm">{localName(s, lang)}</div>
              <div className="text-xs text-muted-foreground mt-1">{s.address || s.city || "—"}</div>
              {s.geo_lat && s.geo_lng ? (
                <a href={`https://www.google.com/maps?q=${s.geo_lat},${s.geo_lng}`} target="_blank" rel="noreferrer"
                   className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-2">
                  <MapPinned className="w-3.5 h-3.5" /> {t("open_map")}
                </a>
              ) : (
                <span className="text-xs text-amber-600 inline-flex items-center gap-1 mt-2">
                  <AlertCircle className="w-3.5 h-3.5" /> {t("geofence_missing")}
                </span>
              )}
            </Card>
          ))}
        </div>
      </Card>
    );
  }

  // With Google Maps API key — use embed with multiple markers via Static Map fallback or interactive
  const center = validItems[0] || { geo_lat: 24.7136, geo_lng: 46.6753 };
  const markers = validItems.map(s => `markers=color:0x117A47%7Clabel:S%7C${s.geo_lat},${s.geo_lng}`).join("&");
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2 p-0 border overflow-hidden shadow-none" style={{ minHeight: 500 }}>
        {validItems.length > 0 ? (
          <iframe
            title="schools-map"
            width="100%" height="500"
            style={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://www.google.com/maps/embed/v1/view?key=${mapsKey}&center=${center.geo_lat},${center.geo_lng}&zoom=6&maptype=roadmap`}
          />
        ) : (
          <div className="p-12 text-center text-muted-foreground">
            <MapPinned className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div className="font-semibold mb-1">{t("no_schools_with_coords")}</div>
            <div className="text-xs">{t("edit_school_geofence_hint")}</div>
          </div>
        )}
      </Card>

      <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
        {items.map(s => (
          <Card key={s.id} className="p-3 border hover:border-primary cursor-pointer transition" onClick={() => onEdit(s)} data-testid={`map-card-${s.id}`}>
            <div className="flex items-start gap-2">
              {s.logo_id ? (
                <img src={`${api.defaults.baseURL}/files/${s.logo_id}`} alt="" className="w-8 h-8 object-contain rounded bg-muted" crossOrigin="anonymous" />
              ) : (
                <div className="w-8 h-8 rounded bg-primary/10 text-primary grid place-items-center"><Building2 className="w-4 h-4" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{localName(s, lang)}</div>
                <div className="text-[11px] text-muted-foreground truncate">{s.city || "—"}</div>
                <div className="flex flex-wrap gap-2 mt-1.5 text-[10px]">
                  <span className="text-emerald-600">{s.stats?.students ?? 0} {t("students")}</span>
                  <span className="text-blue-600">{s.stats?.teachers ?? 0} {t("teachers")}</span>
                  <span className="text-violet-600">{s.stats?.employees ?? 0} {t("employees")}</span>
                </div>
                <div className={`mt-1 text-[10px] inline-flex items-center gap-1 ${s.geofence_configured ? "text-emerald-600" : "text-amber-600"}`}>
                  {s.geofence_configured ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                  {s.geofence_configured ? `${t("geofence_configured")} · ${s.geo_radius_m || 150}م` : t("geofence_missing")}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
